import { createContext, useContext } from "react";

import useSWR from "swr";

import { BaseProp } from "../@types/base-types";
import { DashboardDecimal18 } from "../components/Dashboard/models/decimals/DashboardDecimal18";
import { POLLING_INTERVAL } from "../config";
import { laggy } from "../data/swr-middleware";
import { CollateralPriceMap, getCollateralPriceMap } from "../rpc-contract-service/service";

const URL_FETCH_BAI_PRICE =
  "https://api.diadata.org/v1/assetQuotation/Astar/0x733ebcC6DF85f8266349DEFD0980f8Ced9B45f35";
const URL_FETCH_ATID_PRICE =
  "https://api.diadata.org/v1/assetQuotation/Astar/0x5271D85CE4241b310C0B34b7C2f1f036686A6d7C";

const fetchPrice = async () => {
  const allPrices = await Promise.all([
    window.fetch(URL_FETCH_ATID_PRICE),
    window.fetch(URL_FETCH_BAI_PRICE)
  ]);

  const atidPrice = (await allPrices[0].json()).Price;
  const baiPrice = (await allPrices[1].json()).Price;

  return {
    "astriddao-token": { usd: atidPrice },
    "bai-stablecoin": { usd: baiPrice }
  };
};

export const fetchTokenPrices = async (
  tokens: Readonly<string[]>,
  currencies: Readonly<string[]>,
  isTestnet: boolean
): Promise<CollateralPriceMap> => {
  const collateralPriceMap = await getCollateralPriceMap({ isTestnet });
  const response = await fetchPrice();

  collateralPriceMap.set(
    "ATID",
    DashboardDecimal18.from(response["astriddao-token"].usd ?? DashboardDecimal18.ZERO)
  );
  collateralPriceMap.set(
    "BAI",
    DashboardDecimal18.from(response["bai-stablecoin"].usd ?? DashboardDecimal18.ZERO)
  );
  collateralPriceMap.set(
    "xcBAI",
    DashboardDecimal18.from(response["bai-stablecoin"].usd ?? DashboardDecimal18.ZERO)
  );
  return collateralPriceMap;
};

const useTokenPriceData = (tokens: Readonly<string[]>, isTestnet: boolean) => {
  return useSWR(() => [tokens, ["usd"] as const, isTestnet], fetchTokenPrices, {
    use: [laggy],
    suspense: true,
    refreshInterval: POLLING_INTERVAL,
    revalidateOnFocus: false
  });
};

const TokenPricesContext = createContext<CollateralPriceMap | undefined>(undefined);

export const TokenPricesProvider: React.FC<{ isTestnet: boolean } & BaseProp> = ({
  children,
  isTestnet
}) => {
  const { data } = useTokenPriceData(["astriddao-token", "bai-stablecoin"], isTestnet);

  return <TokenPricesContext.Provider value={data}>{children}</TokenPricesContext.Provider>;
};

export const useTokenPrices = () => {
  const tokenPricesContext = useContext(TokenPricesContext);

  if (!tokenPricesContext) {
    throw new Error("You must provide an TokenPricesContext via TokenPricesProvider");
  }

  return tokenPricesContext;
};
